import { useEffect, useRef, useState } from 'react'

const textValue = value => value == null || !Number.isFinite(Number(value)) ? '' : String(value)

/** Numeric settings keep an editing buffer; only blur/Enter publishes a value.
 * Empty required fields and invalid drafts revert. Optional fields may commit
 * null on blur, but never publish a temporary empty string, zero or NaN.
 */
export default function NumberInput({ value, onValueChange, allowEmpty = false, min, max, step = 1, ...props }) {
  const [draft, setDraft] = useState(() => textValue(value))
  const dirty = useRef(false)
  const inputRef = useRef(null)
  const commitRef = useRef(null)

  // Popovers may unmount on pointerdown, before the native blur event. Commit
  // first so clicking out has the same semantics as tabbing out of the field.
  useEffect(() => {
    const leave = event => {
      const input = inputRef.current
      if (input && input.ownerDocument.activeElement === input && event.target !== input) commitRef.current(input)
    }
    window.addEventListener('pointerdown', leave, true)
    return () => window.removeEventListener('pointerdown', leave, true)
  }, [])

  useEffect(() => {
    setDraft(textValue(value))
    dirty.current = false
  }, [value])

  function reset(input) {
    dirty.current = false
    const text = textValue(value)
    // Also reset the browser's invisible bad-input buffer (e.g. a lone '-').
    input.value = text
    setDraft(text)
  }

  function commit(input) {
    if (!dirty.current) return
    const raw = input.value.trim()
    const number = Number(raw)
    const increment = Number(step)
    const steps = (number - Number(min ?? 0)) / increment
    const stepMismatch = step !== 'any' && increment > 0 && Math.abs(steps - Math.round(steps)) > 1e-7
    if (input.validity.badInput || (raw === '' && !allowEmpty)
      || (raw !== '' && (!Number.isFinite(number)
        || (min != null && number < Number(min)) || (max != null && number > Number(max)) || stepMismatch))) {
      reset(input)
      return
    }
    const next = raw === '' ? null : number
    // The parent owns committed state and may reject the update (e.g. settings
    // are not hydrated yet). Never leave an unsaved candidate looking committed.
    reset(input)
    if (next !== value) onValueChange(next)
  }
  commitRef.current = commit

  return <input {...props} ref={inputRef} type="number" min={min} max={max} step={step} value={dirty.current ? draft : textValue(value)}
    onChange={event => { dirty.current = true; setDraft(event.target.value) }}
    onBlur={event => commit(event.currentTarget)}
    onKeyDown={event => {
      if (event.nativeEvent.isComposing) return
      if (event.key === 'Escape' && !dirty.current) return
      if (event.key === 'Enter' || event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        if (event.key === 'Escape') reset(event.currentTarget)
        else commit(event.currentTarget)
      }
    }} />
}
